// The slides carousel, used by the app onboarding webview, the nav panes, and the website home
// page hero.
//
// IT SUPPORTS TWO SCROLL ARRANGEMENTS, and which one is in play decides what "the scroller" means
// to everything below:
//
//   1. Component-owns-scrollport (default) — `.pane_slides` is itself the scroll container, with
//      `scroll-snap-type: y mandatory`. Correct inside a native webview or a nav pane, where
//      nothing behind the carousel scrolls.
//   2. Page-scroller (`pane_slides--page_scroller`) — the slides sit in normal page flow and the
//      snapping is on the root scroller. Required on an ordinary scrolling web page, because a
//      nested scroll container that snaps TRAPS the page's scroll in Chrome.
//
// The component works in both because it avoids assuming it owns a scrollport: the slides'
// IntersectionObserver uses the default root (the viewport), and movement goes through
// `scrollIntoView` rather than `scrollTo`. **Keep it that way** — anything reading or writing
// `this.element.scrollTop`/`scrollTo` is silently a no-op in arrangement 2.
//
// Full rationale, the cross-engine test matrix and the traps: `doc/README--scroll_snapping.md`.

import Component from "../../lib/component";
import Tracker from "../../lib/tracker";
import {getTrackerOptions} from "../../lib/tracker_helpers";
import {PaneSlide} from "./pane_slide";
import {PaneSlidesBackButton} from "./pane_slides_back_button";
import {PaneSlidesPagination} from "./pane_slides_pagination";

export const PaneSlidesConfig = {
    componentTypeKey: 'PaneSlides',
    baseHtmlClass: 'pane_slides',
    selector: '.pane_slides',

    datasetConfigKey: 'pane_slides__config',

    // A carousel inside a nav pane inherits its identity and its tracking configuration from its
    // surroundings, so existing panes need no markup changes to report their slides.
    navPaneSelector: '[data-nav_pane__identifier]',
    navRootConfigSelector: '[data-nav_root__config]',

    slideFocusedEventName: 'PaneSlides-slideFocused',
    completedEventName: 'PaneSlides-completed',
};

export class PaneSlides extends Component {

    static get config() {
        return PaneSlidesConfig;
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: PaneSlides.config
        });

        this.isSliding = false;

        this.setDatasetConfig(PaneSlidesConfig.datasetConfigKey);
        this.slidesId = this.getSlidesId();
        this.contentVersion = this.getDatasetConfigValue('content_version');
        this.tracker = new Tracker(window, document, getTrackerOptions(this.getTrackerOptionsConfig()) || {});
        this.viewedSlideIndexes = [];
        this.hasCompleted = false;

        // Adopted, so destroying the carousel destroys everything it built. A parent-constructed
        // component never enters the ComponentRegistry, so the reaper cannot find it and this
        // cascade is its ONLY route to a destroy() — and therefore to its registered teardowns.
        this.slides = this.adoptChildComponents(getSlides(this));
        configureNextPrevSlides(this.slides);

        this.configureAccessibility();
        this.liveRegionElement = createLiveRegionElement(this.element);

        this.backButton = this.adoptChildComponents(getBackButton(this));
        this.pagination = this.adoptChildComponents(getPagination(this)); // May be null — adoption passes it through.

        this.focusedSlide = null;
        this.setFocusedSlide(this.slides[0]);

        this.render();
    }

    render() {
        this.conditionallySetHtmlClassState(this.isSliding, 'sliding');
        this.conditionallySetHtmlClassState(this.showScrollHint(), 'scroll_hint');
        this.slides.forEach(slide => slide.render());
        this.backButton.render();
        if (this.pagination) this.pagination.render();
    }

    setFocusedSlide(slide) {
        if (this.focusedSlide) this.focusedSlide.isFocused = false;
        if (slide) {
            this.focusedSlide = slide;
            slide.isFocused = true;
        }
        this.render();
    }

    // Called by a slide when it actually becomes visible (its IntersectionObserver), NOT when it is
    // merely made the focused slide on construction — a pane that has never been opened must not
    // report a slide view.
    handleSlideFocused(slide) {
        const slideIndex = this.slides.indexOf(slide);
        if (slideIndex < 0) return;

        this.element.dispatchEvent(new CustomEvent(PaneSlidesConfig.slideFocusedEventName, {
            bubbles: true,
            detail: {slidesId: this.slidesId, slideId: this.getReportableSlideId(slide), slideIndex: slideIndex},
        }));

        this.announceSlide(slide, slideIndex);
        this.trackSlideViewed(slide, slideIndex);

        if (slideIndex === (this.slides.length - 1)) this.handleCompleted();
    }

    // Carousel semantics (see uxpatterns.dev's carousel pattern): a named region that screen
    // readers describe as a carousel, and a tab stop so the scroller is keyboard-scrollable in
    // every browser (some only auto-focus scrollable regions). Markup-provided attributes win.
    configureAccessibility() {
        if (!this.element.hasAttribute('role')) this.element.setAttribute('role', 'region');
        if (!this.element.hasAttribute('aria-roledescription')) this.element.setAttribute('aria-roledescription', 'carousel');
        if (!this.element.hasAttribute('aria-label')) this.element.setAttribute('aria-label', 'Slides');
        if (!this.element.hasAttribute('tabindex')) this.element.setAttribute('tabindex', '0');
    }

    // Tell screen readers where we are whenever a slide genuinely becomes visible (scroll, swipe
    // or pagination alike — this hangs off the slides' IntersectionObserver).
    announceSlide(slide, slideIndex) {
        if (!this.liveRegionElement) return;
        const position = `Slide ${slideIndex + 1} of ${this.slides.length}`;
        const label = slide.getDatasetConfigValue('label');
        this.liveRegionElement.textContent = label ? `${position}: ${label}` : position;
    }

    // Slide progression for EVERY pane_slides carousel — the onboarding carousel and the info
    // panes' carousels alike. The instance is identified by the `slides_id` parameter rather than
    // by a per-feature event name (see doc/README--analytics.md → Event Naming Conventions).
    trackSlideViewed(slide, slideIndex) {
        if (this.viewedSlideIndexes.indexOf(slideIndex) !== -1) return; // Once per slide per page view.
        this.viewedSlideIndexes.push(slideIndex);
        const reportableSlideId = this.getReportableSlideId(slide);
        this.trackEvent('SLIDE_VIEWED', reportableSlideId, {
            slides_id: this.slidesId,
            slide_id: reportableSlideId,
            slide_index: slideIndex,
        });
    }

    // `slide_id` values are namespaced by the carousel (`ONBOARDING__PEACE_OF_MIND`) so a report
    // broken down by `slide_id` alone stays unambiguous — otherwise every unlabelled carousel's
    // ordinal fallback would collapse into one `SLIDE_1` row across the whole property. Markup
    // keeps the local id; the prefix always matches `slides_id`, composed here at emit time.
    getReportableSlideId(slide) {
        return `${this.slidesId}__${slide.slideId}`;
    }

    handleCompleted() {
        if (this.hasCompleted) return;
        this.hasCompleted = true;

        this.trackEvent('SLIDES_COMPLETED', this.slidesId, {
            slides_id: this.slidesId,
            content_version: this.contentVersion,
        });

        this.element.dispatchEvent(new CustomEvent(PaneSlidesConfig.completedEventName, {
            bubbles: true,
            detail: {slidesId: this.slidesId},
        }));
    }

    // `eventLabel` is included for backwards compatibility with older native apps, which only
    // handled the `eventAction`/`eventLabel` params — see tracker.js `getBackwardsCompatibilityParams()`.
    trackEvent(eventNameKey, eventLabel, parameters) {
        if (!this.slidesId) return; // An unidentifiable carousel would report un-attributable events.
        if (!window.RankersAnalytics || !window.RankersAnalytics.EventName) return;
        const eventName = window.RankersAnalytics.EventName[eventNameKey];
        if (!eventName || !this.tracker) return;
        this.tracker.eventTracker(eventName, {eventLabel: eventLabel, ...parameters});
    }

    // Explicit config (the onboarding carousel) first, then the enclosing nav pane's identifier —
    // which is the same value `NAV_PANE_OPENED` reports, so a pane's opens and its slide views
    // join up in analytics.
    getSlidesId() {
        const configuredSlidesId = this.getDatasetConfigValue('slides_id');
        if (configuredSlidesId) return configuredSlidesId;
        const navPaneElement = this.element.closest(PaneSlidesConfig.navPaneSelector);
        return navPaneElement ? navPaneElement.dataset['nav_pane__identifier'] : null;
    }

    // Likewise: the onboarding carousel is given its tracker options directly; a carousel within
    // the nav takes the nav's (which is where the native-app event bridge is configured).
    getTrackerOptionsConfig() {
        const configuredTrackerOptions = this.getDatasetConfigValue('tracker_options');
        if (configuredTrackerOptions) return configuredTrackerOptions;
        const navRootElement = document.querySelector(PaneSlidesConfig.navRootConfigSelector);
        if (!navRootElement) return null;
        try {
            return JSON.parse(navRootElement.dataset['nav_root__config'])['tracker_options'];
        } catch (error) {
            console.warn('[PaneSlides] Could not read the nav root config for tracker options', error);
            return null;
        }
    }

    // The one-time "Scroll" nudge above the first slide's next arrow. Kept while the traveller
    // has genuinely seen nothing beyond the first slide (viewedSlideIndexes is fed by each
    // slide's IntersectionObserver, not by construction) — once any other slide has been viewed
    // it never shows again.
    showScrollHint() {
        if (this.slides.length < 2) return false;
        return this.viewedSlideIndexes.every(slideIndex => slideIndex === 0);
    }

    isFocusedOnFirstSlide() {
        return this.focusedSlide === this.slides[0];
    }

    // Scrolls whichever scroll container actually holds the slides, rather than assuming it is this
    // element. In the default arrangement `.pane_slides` IS the scrollport; in page-scroller mode
    // (`pane_slides--page_scroller`) the page is, and `this.element.scrollTo` would silently do
    // nothing — which is exactly how the back button came to be dead on the website hero.
    //
    // `scrollIntoView` is the same mechanism `PaneSlide#slideTo` already uses for every chevron and
    // pagination dot, so this makes the back button consistent with its siblings rather than
    // introducing a new one. It resolves against the nearest scrollable ancestor either way.
    goBack() {
        const firstSlide = this.slides[0];
        if (!firstSlide) return;
        firstSlide.element.scrollIntoView({behavior: 'smooth'});
    }

}

function getBackButton(paneSlides) {
    const element = PaneSlidesBackButton.createElement(paneSlides.element);
    return new PaneSlidesBackButton(element, paneSlides);
}

function createLiveRegionElement(parentElement) {
    const element = document.createElement('div');
    element.setAttribute('aria-live', 'polite');
    element.setAttribute('aria-atomic', 'true');
    // Visually hidden (the standard clip technique) — screen readers only.
    element.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;';
    parentElement.appendChild(element);
    return element;
}

// On by default for every carousel with something to paginate; a carousel can opt out with
// `pagination: false` in its config.
function getPagination(paneSlides) {
    if (paneSlides.getDatasetConfigValue('pagination') === false) return null;
    if (paneSlides.slides.length < 2) return null;
    const element = PaneSlidesPagination.createElement(paneSlides.element);
    return new PaneSlidesPagination(element, paneSlides);
}

function getSlides(paneSlides) {
    const elements = Array.from(paneSlides.element.querySelectorAll(PaneSlide.config.selector));
    return elements.map(element => new PaneSlide(element, paneSlides));
}

function configureNextPrevSlides(slides) {
    for (let i = 0; i < slides.length; i++) {
        const paneSlide = slides[i];
        if (i > 0) {
            paneSlide.setPrevSlide(slides[i - 1]);
        }
        if (i < slides.length - 1) {
            paneSlide.setNextSlide(slides[i + 1]);
        }
    }
}
